import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, ArrowRight, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImpactBadge, WOStatusBadge } from '@/components/ui/status-badges';
import type { WorkOrder } from '@/types/maintenance';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { timeInState, queueAgeDays } from '@/lib/workOrderUtils';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { fetchMolds } from '@/lib/api/molds';

interface Props {
  workOrders: WorkOrder[];
}

function prioritySortedWOs(openWOs: WorkOrder[]): WorkOrder[] {
  const lineDown = openWOs.filter(wo => wo.impact === 'line_down');
  const safety = openWOs.filter(wo => wo.impact === 'safety' && wo.impact !== 'line_down');
  const blocked = openWOs
    .filter(wo => wo.status === 'blocked' && wo.impact !== 'line_down' && wo.impact !== 'safety')
    .sort((a, b) => new Date(a.blocked_at || a.created_at).getTime() - new Date(b.blocked_at || b.created_at).getTime());
  const requested = openWOs
    .filter(wo => wo.status === 'requested' && wo.impact !== 'line_down' && wo.impact !== 'safety')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  return [...lineDown, ...safety, ...blocked, ...requested];
}

function priorityReason(wo: WorkOrder): string {
  if (wo.impact === 'line_down') return 'Line Down — highest operational impact';
  if (wo.impact === 'safety') return 'Safety concern — requires immediate attention';
  if (wo.status === 'blocked') return 'Blocked — needs escalation';
  if (wo.status === 'requested') return 'Waiting in queue';
  return 'Pending action';
}

export function NextBestAction({ workOrders }: Props) {
  const { startWork } = useWorkOrders();
  const { user } = useAuth();
  const { settings, updateSettings } = useOrgSettings();

  const isManager = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';
  const openWOs = workOrders.filter(wo => wo.status !== 'closed');
  const autoSorted = prioritySortedWOs(openWOs);

  // For "all clear" check — query overdue PMs
  const { data: molds = [] } = useQuery({
    queryKey: ['molds', user?.organization_id],
    queryFn: () => fetchMolds(user?.organization_id ?? null),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
  const overduePMCount = molds.filter(m => m.pm_due_status === 'red').length;

  // DB-backed priority overrides (org-scoped, shared across all managers)
  const [pinnedOrder, setPinnedOrder] = useState<string[]>(settings.priority_override_order);

  // Sync from DB settings when they load
  useEffect(() => {
    setPinnedOrder(settings.priority_override_order);
  }, [settings.priority_override_order]);

  // Track the settings version we last wrote — for optimistic locking
  const localVersionRef = useRef<number>(settings.priority_override_version);
  useEffect(() => {
    // If the DB version advanced beyond our local version, another manager changed the order
    if (settings.priority_override_version > localVersionRef.current) {
      toast.info('Priority order was updated by another manager — refreshing.');
      setPinnedOrder(settings.priority_override_order);
      localVersionRef.current = settings.priority_override_version;
    }
  }, [settings.priority_override_version, settings.priority_override_order]);

  // Debounced DB write — fires 800ms after the last change
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveToDb = useCallback((order: string[]) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const nextVersion = localVersionRef.current + 1;
      updateSettings({
        priority_override_order: order,
        priority_override_version: nextVersion,
      })
        .then(() => { localVersionRef.current = nextVersion; })
        .catch(() => toast.error('Failed to save priority order'));
    }, 800);
  }, [updateSettings]);

  // Reconcile pinned order with current open WOs
  const displayOrder = useMemo(() => {
    const openMap = new Map(autoSorted.map(wo => [wo.id, wo]));
    const pinned = pinnedOrder.filter(id => openMap.has(id)).map(id => openMap.get(id)!);
    const pinnedSet = new Set(pinnedOrder);
    const remaining = autoSorted.filter(wo => !pinnedSet.has(wo.id));
    return [...pinned, ...remaining];
  }, [autoSorted, pinnedOrder]);

  const moveUp = useCallback((idx: number) => {
    if (idx === 0) return;
    const ids = displayOrder.map(wo => wo.id);
    const next = [...ids];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setPinnedOrder(next);
    saveToDb(next);
  }, [displayOrder, saveToDb]);

  const moveDown = useCallback((idx: number) => {
    if (idx === displayOrder.length - 1) return;
    const ids = displayOrder.map(wo => wo.id);
    const next = [...ids];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    setPinnedOrder(next);
    saveToDb(next);
  }, [displayOrder, saveToDb]);

  const resetOrder = () => {
    setPinnedOrder([]);
    saveToDb([]);
  };

  if (displayOrder.length === 0) {
    if (overduePMCount > 0) {
      return (
        <div className="bg-card rounded-lg border border-border/50 px-5 py-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-amber))]" />
            <p className="text-sm font-semibold">No open work orders</p>
          </div>
          <p className="text-xs text-muted-foreground">
            {overduePMCount} mold{overduePMCount !== 1 ? 's have' : ' has'} overdue PM — no work orders created yet.{' '}
            <Link to="/maintenance/molds?status=overdue" className="text-primary hover:underline">
              View overdue molds →
            </Link>
          </p>
        </div>
      );
    }
    return (
      <div className="bg-card rounded-lg border border-border/50 px-5 py-4">
        <p className="text-sm text-muted-foreground text-center">No pending actions — all clear.</p>
      </div>
    );
  }

  // Manager mode: top 5 stacked list with manual reorder
  if (isManager) {
    const top5 = displayOrder.slice(0, 5);
    return (
      <div className="bg-card rounded-lg border border-border/50 px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-amber))]" />
          <h2 className="text-sm font-semibold">Top Priorities</h2>
          {pinnedOrder.length > 0 && (
            <button
              onClick={resetOrder}
              className="ml-1 text-[10px] text-muted-foreground/50 hover:text-muted-foreground flex items-center gap-0.5 transition-colors"
            >
              <RotateCcw className="h-2.5 w-2.5" />
              Reset order
            </button>
          )}
          <span className="ml-auto text-[11px] text-muted-foreground/60">{displayOrder.length} open</span>
        </div>
        <div className="space-y-1">
          {top5.map((wo, idx) => (
            <div key={wo.id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors group">
              {/* Reorder controls */}
              <div className="flex flex-col gap-0 shrink-0">
                <button
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  className="p-0.5 rounded hover:bg-muted disabled:opacity-20 disabled:cursor-not-allowed text-muted-foreground/50 hover:text-foreground"
                >
                  <ArrowUp className="h-2.5 w-2.5" />
                </button>
                <button
                  onClick={() => moveDown(idx)}
                  disabled={idx === displayOrder.length - 1}
                  className="p-0.5 rounded hover:bg-muted disabled:opacity-20 disabled:cursor-not-allowed text-muted-foreground/50 hover:text-foreground"
                >
                  <ArrowDown className="h-2.5 w-2.5" />
                </button>
              </div>

              <span className="text-[11px] text-muted-foreground/50 w-3 shrink-0">{idx + 1}</span>

              <Link
                to={`/maintenance/work-orders/${wo.id}`}
                className="flex-1 min-w-0 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                    <span className="font-medium text-xs">{wo.mold?.mold_id || wo.mold_id}</span>
                    <ImpactBadge impact={wo.impact} size="sm" />
                    <WOStatusBadge status={wo.status} size="sm" />
                    {wo.machine && (
                      <span className="text-[10px] text-muted-foreground/50">@ {wo.machine.name}</span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {wo.failure_text || wo.problem_description}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0 text-[10px] text-muted-foreground/60">
                  {wo.assignee && <span>{wo.assignee.name}</span>}
                  <span className="flex items-center gap-0.5">
                    <Clock className="h-2.5 w-2.5" />
                    {timeInState(wo)}
                  </span>
                  <span className="text-muted-foreground/40">{queueAgeDays(wo)}d total</span>
                </div>
                <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0 group-hover:text-primary transition-colors" />
              </Link>
            </div>
          ))}
        </div>
        {displayOrder.length > 5 && (
          <p className="text-[11px] text-muted-foreground/50 mt-2 text-center">
            +{displayOrder.length - 5} more open work orders
          </p>
        )}
      </div>
    );
  }

  // Technician mode: single recommended WO
  // Exclude in_progress WOs assigned to someone else — those are already claimed
  const actionable = displayOrder.filter(
    wo => wo.status !== 'in_progress' || !wo.assigned_to || wo.assigned_to === user?.id
  );

  if (actionable.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border/50 px-5 py-4">
        <p className="text-sm text-muted-foreground text-center">All active work orders are assigned — check with your manager.</p>
      </div>
    );
  }

  const recommended = actionable[0];
  const reason = priorityReason(recommended);

  const cta = recommended.status === 'requested'
    ? { label: 'Start Work', action: () => startWork(recommended.id).catch(() => toast.error('Failed to start work order')) }
    : recommended.status === 'blocked'
    ? { label: 'Open & Resolve', link: `/work-orders/${recommended.id}` }
    : { label: 'Open WO', link: `/work-orders/${recommended.id}` };

  return (
    <div className="bg-card rounded-lg border border-border/50 px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-amber))]" />
        <h2 className="text-sm font-semibold">Next Best Action</h2>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-sm">{recommended.mold?.mold_id || recommended.mold_id}</span>
            <ImpactBadge impact={recommended.impact} size="sm" />
            {recommended.machine && (
              <span className="text-[10px] text-muted-foreground/50">@ {recommended.machine.name}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mb-1">
            {recommended.failure_text || recommended.problem_description}
          </p>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground/70">
            {recommended.assignee && <span>{recommended.assignee.name}</span>}
            <span className="flex items-center gap-0.5">
              <Clock className="h-3 w-3" />
              {timeInState(recommended)} in {recommended.status.replace('_', ' ')}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {cta.link ? (
            <Button asChild size="sm" className="h-7 text-xs px-3">
              <Link to={cta.link}>
                {cta.label}
                <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          ) : (
            <Button size="sm" className="h-7 text-xs px-3" onClick={cta.action}>
              {cta.label}
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground/60 mt-2">Reason: {reason}</p>
    </div>
  );
}
